import type { Geometry } from 'geojson';

import {
  isAreaGeometry,
  parseAiSpatialFilter,
  type AiSpatialFilter,
} from 'components/DisasterGPT/utils/aiMapSpatialFilter';

export const CUSTOM_AREA_LABEL = 'Custom area';

export type SitrepSpatialScope = {
  spatialFilter: AiSpatialFilter;
  spatialFilterLabel: string;
  source: 'incident' | 'aor' | 'asset' | 'custom';
};

type DefaultScopeSitrep = {
  aor_id: string | null;
  incident_id: string | null;
  subject_kind?: string | null;
  spatial_filter?: unknown;
  spatial_filter_label?: string | null;
  scope_source?: string | null;
};

/**
 * The area saved on an asset or custom-area report, which is that report's
 * default.
 */
function savedSubjectArea(
  sitrep: DefaultScopeSitrep
): SitrepSpatialScope | null {
  const isAssetArea =
    sitrep.subject_kind === 'asset' && sitrep.scope_source === 'asset';
  if (!isAssetArea && sitrep.subject_kind !== 'custom') return null;
  const area = parseAiSpatialFilter(sitrep.spatial_filter);
  if (!area || !isAreaGeometry(area)) return null;
  return {
    spatialFilter: area,
    spatialFilterLabel:
      sitrep.spatial_filter_label?.trim() ||
      (isAssetArea ? 'Asset area' : CUSTOM_AREA_LABEL),
    source: isAssetArea ? 'asset' : 'custom',
  };
}

export function parseAorLocationGeom(
  raw: string | null | undefined
): Geometry | undefined {
  if (!raw?.trim()) return undefined;
  try {
    return JSON.parse(raw) as Geometry;
  } catch {
    return undefined;
  }
}

export function isCustomSpatialScope(
  label: string | null | undefined
): boolean {
  return label?.trim() === CUSTOM_AREA_LABEL;
}

/**
 * Default Analytics geofence for a sitrep. An incident report uses the incident
 * polygon only; a missing or point location does not fall back to the AOR
 * linked on that incident. An AOR report uses the AOR polygon. Asset and
 * custom-area reports use the area saved on the report.
 */
export function resolveSitrepDefaultSpatialFilter(params: {
  sitrep: DefaultScopeSitrep;
  incident?: { name: string; location?: Geometry | null } | null;
  aor?: { name: string; current_location_geom?: string | null } | null;
}): SitrepSpatialScope | null {
  if (
    params.sitrep.subject_kind === 'asset' ||
    params.sitrep.subject_kind === 'custom'
  ) {
    return savedSubjectArea(params.sitrep);
  }

  if (params.sitrep.incident_id) {
    const incidentArea = params.incident
      ? parseAiSpatialFilter(params.incident.location)
      : undefined;
    if (incidentArea && isAreaGeometry(incidentArea)) {
      return {
        spatialFilter: incidentArea,
        spatialFilterLabel: `Incident: ${params.incident?.name?.trim() || 'Incident'}`,
        source: 'incident',
      };
    }
    return null;
  }

  const aorGeom = params.sitrep.aor_id
    ? parseAorLocationGeom(params.aor?.current_location_geom)
    : undefined;
  const aorArea = parseAiSpatialFilter(aorGeom);
  if (aorArea && isAreaGeometry(aorArea)) {
    return {
      spatialFilter: aorArea,
      spatialFilterLabel: `AOR: ${params.aor?.name?.trim() || 'AOR'}`,
      source: 'aor',
    };
  }

  return null;
}
